import {
  SwitchProps,
} from '@material-ui/core';
import {
  KeyboardDatePickerProps, KeyboardTimePickerProps,
} from '@material-ui/pickers';
import {
  EditorProps,
} from 'react-draft-wysiwyg';
import * as Yup from 'yup';
import {
  ModelOf,
} from '../../helperFunctions/types';
import {
  BaseFormViewModel,
} from '../../screens/types';
import {
  DTO,
} from '../../stores/types';
import Condition from './Utils/Condition';

export enum GeneralFormFields {
  INPUT_FIELD = 'inputField',
  TEXT_AREA = 'textArea',
  FILE_PICKER = 'filePicker',
  NESTED_FORM = 'nestedForm',
  FIELD_ARRAY = 'fieldArray',
  SELECT_FIELD = 'selectField',
  RICH_TEXT_FIELD = 'richTextField',
  DATE_TIME_FIELD = 'dateTimeField',
  TOGGLE_FIELD = 'toggleField',
  LOCATION_FIELD = 'locationField',
  COLOR_FIELD = 'colorField',
}

export enum AvailableLanguages {
  ARABIC = 'ar',
  ENGLISH = 'en',
}

export type Props<EntityDTO extends DTO, FormViewModel extends BaseFormViewModel<EntityDTO>> = {
  formData: FieldsData<FormViewModel>;
  defaultLang: string;
  otherLanguages: string[];
  title: string;
  identifier: keyof ModelOf<FormViewModel>;
  get: (identifier: string, language: string) => Promise<FormViewModel>;
  create: (formValue: FormViewModel) => Promise<number | string>;
  update: (identifier: string, formValue: FormViewModel, language: string) => Promise<void>;
  isFetchSuccessful: boolean;
  createOrUpdateStatus?: RequestStatus;
  postSubmissionRoute?: string;
};

export enum RequestStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  FAILED = 'failed',
}

export interface FormCustomPreview<T> {
  component: React.ComponentType<T>;
}

export interface RouteParams {
  id: string;
}

export interface GeneralFormFieldProperties {
  initialValue: any;
  validationSchema: Yup.Schema<any>;
  component: any;
}

export type FieldsData<T, K extends keyof T = keyof T, V = T> = (FormFieldData<T, K> | FormCustomPreview<V>)[];

export function isCustomFormPreview<T>(data: any): data is FormCustomPreview<T> {
  return !!(data as FormCustomPreview<T>).component;
}

export function isFormField<T, K extends keyof T = keyof T>(data: any): data is FormFieldData<T, K> {
  return !!(data as FormFieldData<T, K>).key;
}

export type FormDataObject<T> = {
  [K in keyof Partial<T>]: FormFieldData<T, K>;
};

export type InputFieldOptions = {
};

export interface InputFormField {
  type: GeneralFormFields.INPUT_FIELD;
  fieldOptions: InputFieldOptions;
}

export type TextAreaFieldOptions = {
};

export interface TextAreaFormField {
  type: GeneralFormFields.TEXT_AREA;
  fieldOptions: TextAreaFieldOptions;
}

export interface ImagePickerFieldOption {
  ratio?: number;
  size: number;
  accept: string;
  multipleImages: boolean;
}

export interface ImagePickerFormField {
  type: GeneralFormFields.FILE_PICKER;
  fieldOptions: ImagePickerFieldOption;
}

export interface NestedFormFieldOptions<T> {
  formData: FieldsData<T>;
  isCollapsible?: boolean;
  titleColumn?: string;
  isCollapsibleInArray?: boolean;
}

export interface NestedFormFormField<T, K extends keyof T> {
  type: GeneralFormFields.NESTED_FORM;
  fieldOptions: NestedFormFieldOptions<T[K]>;
}

export interface FormFieldArrayOptions<T, K extends keyof T> {
  formData: KeylessFormFieldData<T[K] extends unknown[] ? T[K] : any[], number>;
  isCollapsible?: boolean;
}

export interface FormFieldArray<T, K extends keyof T> {
  type: GeneralFormFields.FIELD_ARRAY;
  fieldOptions: FormFieldArrayOptions<T, K>;
}

export interface SelectFieldItems<T> {
  title: string;
  value: T;
}

export interface SelectFieldOptions<T, K = T extends unknown[] ? T[number] : any> {
  items: Promise<SelectFieldItems<K>[]> | SelectFieldItems<K>[];
  isMultiple: boolean;
}

export type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number];

export interface SelectField<T> {
  type: GeneralFormFields.SELECT_FIELD;
  fieldOptions: SelectFieldOptions<T>;
}

export interface RichTextFieldOptions {
  editorProps?: EditorProps;
}

export interface RichTextFormField {
  type: GeneralFormFields.RICH_TEXT_FIELD;
  fieldOptions: RichTextFieldOptions;
}

export enum DateTimeFormFieldTypes {
  DATE,
  TIME,
  DATE_TIME,
}

export interface DateOnlyFormFieldOptions {
  type: DateTimeFormFieldTypes.DATE;
  datePickerProps?: KeyboardDatePickerProps;
}

export interface TimeOnlyFormFieldOptions {
  type: DateTimeFormFieldTypes.TIME;
  timePickerProps?: KeyboardTimePickerProps;
}

export interface DateAndTimeFormFieldOptions {
  type: DateTimeFormFieldTypes.DATE_TIME;
  datePickerProps?: KeyboardDatePickerProps;
  timePickerProps?: KeyboardTimePickerProps;
}

export type DateTimeFormFieldOptions = DateOnlyFormFieldOptions
| TimeOnlyFormFieldOptions
| DateAndTimeFormFieldOptions;

export function isDateOnlyPicker(fieldOptions: DateTimeFormFieldOptions): fieldOptions is DateOnlyFormFieldOptions {
  return fieldOptions.type === DateTimeFormFieldTypes.DATE;
}

export function isTimeOnlyPicker(fieldOptions: DateTimeFormFieldOptions): fieldOptions is TimeOnlyFormFieldOptions {
  return fieldOptions.type === DateTimeFormFieldTypes.TIME;
}

export function isDateAndTimePicker(
  fieldOptions: DateTimeFormFieldOptions,
): fieldOptions is DateAndTimeFormFieldOptions {
  return fieldOptions.type === DateTimeFormFieldTypes.DATE_TIME;
}

export interface DateTimeFormField {
  type: GeneralFormFields.DATE_TIME_FIELD;
  fieldOptions: DateTimeFormFieldOptions;
}

export interface ToggleFormFieldOptions {
  switchProps?: SwitchProps;
}

export interface ToggleFormField {
  type: GeneralFormFields.TOGGLE_FIELD;
  fieldOptions: ToggleFormFieldOptions;
}

export type LocationFormFieldOptions = {
};

export interface LocationFormField {
  type: GeneralFormFields.LOCATION_FIELD;
  fieldOptions: LocationFormFieldOptions;
}

export type ColorFormFieldOptions = {
};

export interface ColorFormField {
  type: GeneralFormFields.COLOR_FIELD;
  fieldOptions: ColorFormFieldOptions;
}

export interface MapLocation {
  longitude: number;
  latitude: number;
}

export interface FormDataCommon<T, K extends keyof T = keyof T> extends KeylessFormDataCommon<T, K> {
  key: K;
}

export interface KeylessFormDataCommon<T, K extends keyof T = keyof T> {
  title: string;
  hasTranslations?: boolean;
  condition?: (values: any, actions: Record<FormFieldActions, () => void>, currentPath: string) => Condition;
  validationSchema?: Yup.Schema<T[K]> | Yup.NotRequiredArraySchema<any>;
  initialValue?: T[K];
}

export enum FormFieldActions {
  ENABLE,
  DISABLE,
  HIDE,
}

export enum FormFieldStatus {
  ENABLED,
  DISABLED,
  HIDDEN,
}

export type FormFieldOptions<T, K extends keyof T, K2 extends keyof T[K]> = InputFormField
| TextAreaFormField
| ImagePickerFormField
| NestedFormFormField<T, K>
| FormFieldArray<T, K>
| SelectField<T[K]>
| RichTextFormField
| DateTimeFormField
| ToggleFormField
| LocationFormField
| ColorFormField;

export type FormFieldData<
  T,
  K extends keyof T,
  K2 extends keyof T[K] = keyof T[K]
  > = FormDataCommon<T, K> & FormFieldOptions<T, K, K2>;
export type KeylessFormFieldData<
  T,
  K extends keyof T,
  K2 extends keyof T[K] = keyof T[K]
  > = KeylessFormDataCommon<T, K> & FormFieldOptions<T, K, K2>;

export default GeneralFormFields;
